import type { ReactNode } from 'react'
import { Sparkles, TriangleAlert } from 'lucide-react'
import ThemeToggle from '@/components/layout/ThemeToggle'
import { cn } from '@/lib/cn'

/** Alpha-only mask, so the hairline grid fades out instead of ending on a hard edge. */
const GRID_MASK = 'radial-gradient(ellipse 72% 58% at 50% 0%, #000 25%, transparent 78%)'

/**
 * Decorative only. The grid is drawn from `--color-line` rather than a literal
 * colour, which is what keeps it correct in both themes.
 */
function Backdrop(): React.ReactElement {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-brand-500/10 to-transparent dark:from-brand-500/15" />
      <div
        className="absolute inset-0 opacity-80 dark:opacity-50"
        style={{
          backgroundImage:
            'linear-gradient(to right, var(--color-line) 1px, transparent 1px), linear-gradient(to bottom, var(--color-line) 1px, transparent 1px)',
          backgroundSize: '72px 72px',
          maskImage: GRID_MASK,
          WebkitMaskImage: GRID_MASK
        }}
      />
      <div className="absolute -top-56 left-1/2 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-brand-400/25 blur-[110px] dark:bg-brand-600/25" />
      <div className="absolute -bottom-40 -right-32 h-[24rem] w-[24rem] rounded-full bg-info-500/12 blur-[110px] dark:bg-brand-800/35" />
      <div className="absolute -bottom-36 -left-28 h-[20rem] w-[20rem] rounded-full bg-brand-300/20 blur-[100px] dark:bg-info-700/20" />
    </div>
  )
}

export interface AuthLayoutProps {
  /** One line under the wordmark. Overridden on first-run setup. */
  tagline?: string
  children: ReactNode
}

/**
 * Full-page shell for the unauthenticated screens. `min-h-dvh` (never `h-dvh`)
 * so a short landscape viewport scrolls the whole thing instead of clipping it.
 */
export default function AuthLayout({
  tagline = 'Accessible alt text for WordPress media libraries, at scale.',
  children
}: AuthLayoutProps): React.ReactElement {
  return (
    <div className="relative min-h-dvh w-full bg-canvas">
      <Backdrop />

      <div className="relative mx-auto flex min-h-dvh w-full max-w-md flex-col px-4 py-5 sm:px-6 sm:py-7">
        <div className="flex shrink-0 justify-end">
          <ThemeToggle />
        </div>

        <div className="flex flex-1 flex-col justify-center gap-6 py-7 sm:gap-7 sm:py-10">
          <header className="flex flex-col items-center gap-3 text-center">
            <span className="grid size-11 place-items-center rounded-[0.85rem] bg-accent text-accent-fg shadow-pop ring-1 ring-brand-500/30">
              <Sparkles className="size-[1.35rem]" aria-hidden="true" />
            </span>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-fg">Argus</h1>
              <p className="mx-auto mt-1.5 max-w-[19rem] text-sm leading-relaxed text-fg-muted">
                {tagline}
              </p>
            </div>
          </header>

          <main>{children}</main>
        </div>

        <footer className="shrink-0 space-y-1 text-center text-[11px] leading-relaxed text-fg-subtle">
          <p>Self-hosted — your SSH keys never leave this instance.</p>
          <p>
            Made by{' '}
            <a
              href="https://9cloudwebworks.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-accent underline decoration-transparent underline-offset-2 transition-colors hover:decoration-current"
            >
              Wasim Sayyed
            </a>
          </p>
        </footer>
      </div>
    </div>
  )
}

export type AuthNoticeTone = 'danger' | 'info'

export interface AuthNoticeProps {
  tone?: AuthNoticeTone
  title?: string
  children: ReactNode
}

const NOTICE_TONES: Record<AuthNoticeTone, string> = {
  danger: 'border-bad-500/40 bg-bad-100/70 text-bad-700 dark:bg-bad-950/50 dark:text-bad-300',
  info: 'border-line bg-sunken text-fg-muted'
}

/**
 * Shared by the auth forms, which all surface the same shape of failure. Lives
 * here because it is only ever used alongside this layout's screens.
 */
export function AuthNotice({
  tone = 'danger',
  title,
  children
}: AuthNoticeProps): React.ReactElement {
  return (
    <div
      role={tone === 'danger' ? 'alert' : undefined}
      className={cn(
        'flex items-start gap-2.5 rounded-control border px-3 py-2.5 text-sm',
        NOTICE_TONES[tone]
      )}
    >
      {tone === 'danger' ? (
        <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      ) : null}
      <div className="min-w-0 flex-1 space-y-1 leading-relaxed">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children}
      </div>
    </div>
  )
}

/**
 * Flattens `ApiClientError.details` to one message per field, so a form can bind
 * server validation to the input that caused it.
 */
export function fieldErrorsFrom(
  details: Record<string, string[]> | undefined
): Record<string, string> {
  const errors: Record<string, string> = {}
  if (!details) return errors

  for (const [field, messages] of Object.entries(details)) {
    const first = messages[0]
    if (first) errors[field] = first
  }

  return errors
}

export { AuthLayout }
